import React from 'react';
import { Box, Button, HStack, Icon, Text } from '@chakra-ui/react';
import { IconButton } from '@/components/ui/icon-button';
import { LuBookOpen, LuCircleAlert, LuX } from 'react-icons/lu';
import {
	getAskReadinessCopy,
	hasKnowledgeBaseFailure,
	KnowledgeBaseReadinessState,
	type KnowledgeBaseReadinessState as KnowledgeBaseReadinessStateValue,
} from '@/hooks/house/documents/knowledgeBaseReadiness';
import { DELAYED_READINESS_SHIMMER_DURATION_MS, getComposerAdvisoryTransitionCss } from '@/components/ui/motion';
import ShimmerOverlay from '@/components/common/ShimmerOverlay';

interface AskReadinessAdvisoryProps {
	state: KnowledgeBaseReadinessStateValue;
	fileCount?: number;
	placement: 'above' | 'below';
	visible: boolean;
	onViewRecords?: () => void;
	viewRecordsLabel?: 'View file' | 'View records';
	onDismiss?: () => void;
}

const AskReadinessAdvisory: React.FC<AskReadinessAdvisoryProps> = ({
	state,
	fileCount,
	placement,
	visible,
	onViewRecords,
	viewRecordsLabel = 'View records',
	onDismiss,
}) => {
	const failed = hasKnowledgeBaseFailure(state);
	const stalled = state === KnowledgeBaseReadinessState.Stalled;
	const needsAttention = failed || stalled;
	const isAbove = placement === 'above';
	const { advisory, advisoryMobile } = getAskReadinessCopy(state, fileCount);
	const hasResponsiveCopy = advisory !== advisoryMobile;

	return (
		<HStack
			role="status"
			aria-live="polite"
			align="flex-start"
			gap={2}
			position="relative"
			zIndex={0}
			mx={3}
			mt={isAbove ? 0 : '-12px'}
			mb={isAbove ? '-12px' : 0}
			px={3}
			pt={isAbove ? 2 : '20px'}
			pb={isAbove ? '20px' : 2}
			borderWidth="1px"
			borderColor="border.subtle"
			borderRadius="2xl"
			borderTopRadius={isAbove ? '2xl' : 0}
			borderBottomRadius={isAbove ? 0 : '2xl'}
			bg="surface.subtle"
			color={failed ? 'text.error' : stalled ? 'orange.600' : 'fg.muted'}
			css={getComposerAdvisoryTransitionCss(placement, visible)}
		>
			<Box
				boxSize="20px"
				display="flex"
				alignItems="center"
				justifyContent="center"
				position="relative"
				overflow="hidden"
				flexShrink={0}
			>
				<Icon as={needsAttention ? LuCircleAlert : LuBookOpen} boxSize={4} />
				{!needsAttention && (
					<ShimmerOverlay
						style={
							state === KnowledgeBaseReadinessState.Delayed
								? { animationDuration: `${DELAYED_READINESS_SHIMMER_DURATION_MS}ms` }
								: undefined
						}
					/>
				)}
			</Box>
			<Text fontSize="xs" lineHeight="20px" flex={1} minW={0}>
				{hasResponsiveCopy ? (
					<>
						<Box as="span" display={{ base: 'inline', md: 'none' }}>
							{advisoryMobile}
						</Box>
						<Box as="span" display={{ base: 'none', md: 'inline' }}>
							{advisory}
						</Box>
					</>
				) : (
					advisory
				)}
				{onViewRecords && (
					<Button
						type="button"
						variant="plain"
						size="xs"
						h="20px"
						minW={0}
						p={0}
						ml={1}
						display={{ base: 'inline', md: 'none' }}
						verticalAlign="baseline"
						textStyle="micro.strong"
						lineHeight="20px"
						color="inherit"
						whiteSpace="nowrap"
						textDecoration="underline"
						textUnderlineOffset="2px"
						onClick={onViewRecords}
					>
						{viewRecordsLabel}
					</Button>
				)}
			</Text>
			{(onViewRecords || onDismiss) && (
				<HStack gap={1} flexShrink={0} align="center" display={{ base: 'none', md: 'flex' }}>
					{onViewRecords && (
						<Button
							type="button"
							variant="plain"
							size="xs"
							h="20px"
							minW={0}
							p={0}
							textStyle="micro.strong"
							lineHeight="20px"
							color="inherit"
							whiteSpace="nowrap"
							textDecoration="underline"
							textUnderlineOffset="2px"
							onClick={onViewRecords}
						>
							{viewRecordsLabel}
						</Button>
					)}
					{onDismiss && (
						<IconButton
							type="button"
							aria-label="Dismiss processing notice"
							variant="ghost"
							boxSize="20px"
							minW="20px"
							minH="20px"
							p={0}
							flexShrink={0}
							color="fg.muted"
							_hover={{ bg: 'surface.default', color: 'brand.fg' }}
							css={{ '& svg': { width: '12px', height: '12px' } }}
							onClick={onDismiss}
						>
							<LuX />
						</IconButton>
					)}
				</HStack>
			)}
		</HStack>
	);
};

export default AskReadinessAdvisory;
